import { gsap } from "gsap";
import Emitter from "../utils/emitter";
import debounce from "lodash.debounce";
import React, { useState, useRef, useEffect } from "react";

import Image2021 from 'static/img/menu/2021.jpg';
import Image2020 from 'static/img/menu/2020.jpg';
import Image2019 from 'static/img/menu/2019.jpg';
import Image2018 from 'static/img/menu/2018.jpg';

const Navigation = () => {
  let rafId = null;
  let navLinks = [];
  let navLines = [];
  let navImages = [];
  let cursorTl = null;
  let hovering = false;
  let hideTween = null;
  let showTween = null;
  let cursorBounds = null;
  let isTouchDevice = false;
  let mouse = { x: 0, y: 0 };
  let [navToggleState, setNavToggleState] = useState(false);

  let hoverIndex = null;

  const list = useRef();
  const cursor = useRef();

  const checkTouchDevice = () =>
    "ontouchstart" in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0;

  const setHoverIndex = (index) => {
    if (isTouchDevice) return;
    hideTween && hideTween.kill();
    showTween && showTween.kill();

    const hideLines = navLines.filter((el, itemIndex) => itemIndex !== index);
    const hideLinks = navLinks.filter((el, itemIndex) => itemIndex !== index);
    const hideImages = navImages.filter((el, itemIndex) => itemIndex !== index);

    hideTween = gsap
      .timeline()
      .to(hideImages, { autoAlpha: 0 }, 0)
      .to(hideLinks, { autoAlpha: .4, color: "#2E2F30" }, 0);
    showTween = gsap
      .timeline()
      .to(navImages[index], { autoAlpha: 1 }, 0)
      .to(navLinks[index], { color: index == 3 ? "#2E2F30" : "white", autoAlpha: 1 }, 0);
  };

  const close = (ev, hash) => {
    if (window.location.href == window.location.origin) {
      ev.preventDefault();
      Emitter.emit("nav:click", hash);
    }
    setNavToggleState(false);
  };

  const handleMouseEnter = () => {
    if (hovering || isTouchDevice) return;
    hovering = true;
    cursorTl.timeScale(1).play();
  };

  const handleMouseMove = (ev) => {
    mouse = { x: ev.clientX, y: ev.clientY };
  };

  const handleMouseLeave = () => {
    if (!hovering || isTouchDevice) return;
    hovering = false;
    cursorTl.pause();
    cursorTl.progress(0);

    hideTween && hideTween.kill();
    showTween && showTween.kill();

    hideTween = gsap
      .timeline()
      .to(navImages, { autoAlpha: 0 }, 0)
      .to(navLinks, { autoAlpha: 1, color: "black" }, 0);
  };

  const tick = () => {
    cursor.current.style.transform = `translate3d(${mouse.x - cursorBounds.width / 2}px,${
      mouse.y - cursorBounds.height / 2
    }px,0)`;
    rafId = requestAnimationFrame(tick);
  };

  const resize = () => {
    cursorBounds = cursor.current.getBoundingClientRect();
  };

  const addEventListeners = () => {
    window.addEventListener("resize", debounce(resize, 250));
  };

  const removeEventListeners = () => {
    window.removeEventListener("resize", debounce(resize, 250));
  };

  useEffect(() => {
    isTouchDevice = checkTouchDevice();
    navImages = [...document.querySelectorAll(".nav__image")];
    navLinks = [...document.querySelectorAll(".page-links .nav__link")];
    navLines = [...document.querySelectorAll(".page-links .nav__item__lines")];

    resize();
    tick();
    addEventListeners();

    cursorTl = gsap
      .timeline({ paused: true })
      .fromTo(cursor.current, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.2 });

    return () => {
      removeEventListeners();
      rafId && cancelAnimationFrame(rafId);
    };
  });
  return (
    <div className={`nav ${navToggleState ? "is-open" : ""}`} onMouseMove={handleMouseMove}>
      
        <div className="nav__logo">
          <a href="/ideas-report/">
            <svg viewBox="0 0 190 28" fill="none">
              <path d="M38.2822 1.09436C36.8413 2.59672 36.4155 3.39689 35.384 5.66676L25.8708 27.4836L19.1411 12.1987L12.4933 27.4836L2.89818 5.68308C1.91575 3.41322 1.47365 2.67837 0 1.09436H12.1658C11.2489 1.86187 10.774 2.858 10.774 4.09908C10.774 4.9809 10.9214 5.60143 11.3962 6.74453L15.0312 15.3994L17.7166 8.94908L16.2102 5.45447C15.326 3.44588 14.7038 2.18847 13.8196 1.09436H25.9854C24.8065 1.86187 24.217 2.858 24.217 4.09908C24.217 4.9809 24.3644 5.60143 24.8392 6.74453L28.5561 15.3994L32.2238 6.74453C32.6987 5.60143 32.8788 4.9809 32.8788 4.14807C32.8788 3.05396 32.1092 1.73123 31.225 1.11069H38.2822V1.09436Z"></path>
              <path d="M50.9231 20.5268C49.7442 25.6217 46.9115 27.3037 42.8016 27.3037C38.1023 27.3037 33.6978 23.8908 33.6978 17.4894C33.6978 11.333 37.5947 6.9729 42.7034 6.9729C46.0437 6.9729 50.743 8.68755 50.743 15.9544H39.6415C40.411 19.8409 42.949 21.8495 46.4694 21.8495C48.2705 21.8332 49.5313 21.5066 50.9231 20.5268ZM39.4286 13.4069V13.8805H44.2753C44.2753 10.2226 43.473 9.07947 41.9174 9.07947C40.3456 9.07947 39.4286 10.6635 39.4286 13.4069Z"></path>
              <path d="M73.1911 7.72408C70.1456 4.83367 67.1328 3.33132 64.8077 3.33132V19.498C64.8077 22.764 65.6592 24.7399 67.886 26.7485H54.0009C56.2442 24.7399 57.0792 22.7477 57.0792 19.498V3.33132C54.7705 3.33132 51.6104 4.83367 48.6958 7.72408L50.1695 0C51.1683 0.767509 53.0021 1.09411 55.0489 1.09411H66.8053C68.8684 1.09411 70.7023 0.767509 71.6847 0L73.1911 7.72408Z"></path>
              <path d="M81.6404 26.7486H69.9494C71.3903 25.0666 72.0125 23.9562 72.0125 21.2944V15.3503C72.0125 13.26 71.7178 12.2802 70.326 10.9902L69.5237 10.2553L78.3492 6.95667V11.5291C79.2007 8.8836 80.5106 6.95667 82.6883 6.95667C84.4567 6.95667 85.5865 8.19774 85.5865 10.0267C85.5865 11.97 84.3748 13.3254 82.6883 13.3254C82.3281 12.4762 81.4439 12.0843 80.6252 12.0843C79.9702 12.0843 79.3316 12.2639 79.0042 12.5905V21.0658C78.9878 23.8909 79.4626 24.6584 81.6404 26.7486Z"></path>
              <path d="M105.775 23.3845C105.333 25.6871 103.418 27.3037 100.814 27.3037C98.6036 27.3037 96.9171 25.981 96.4095 24.4786C95.3124 26.4546 93.2493 27.3037 91.0388 27.3037C87.9933 27.3037 85.9302 25.2951 85.9302 22.5354C85.9302 19.3184 88.2389 17.0812 93.5768 15.7911L96.0329 15.2032V11.9046C96.0329 9.84698 95.3288 9.01415 94.0844 9.01415C92.9055 9.01415 92.1359 9.78166 92.1359 10.8431C92.1359 11.7576 92.578 12.3455 93.3476 13.0803C93.3476 14.2561 91.5137 15.4645 89.5979 15.4645C87.7313 15.4645 86.405 14.0275 86.405 12.2801C86.405 9.16112 89.8599 6.9729 94.9358 6.9729C100.225 6.9729 103.025 9.21011 103.025 14.1908V22.0618C103.025 23.0579 103.614 23.7111 104.531 23.7111C105.071 23.7111 105.481 23.5968 105.775 23.3845ZM96.0329 22.8293V16.9342L95.558 17.0812C93.9861 17.5547 92.9055 18.5835 92.9055 20.6411C92.9055 22.4047 93.6095 23.4662 94.7393 23.4662C95.3452 23.4499 95.82 23.2702 96.0329 22.8293Z"></path>
              <path d="M115.731 26.7486H105.841C106.872 25.7525 107.527 24.2991 107.527 21.2944V15.3503C107.527 13.3417 107.265 12.3129 106.054 11.1698L105.202 10.4023L114.306 6.95667V9.81441C115.37 8.05077 117.614 6.95667 119.857 6.95667C123.377 6.95667 125.326 8.86727 125.326 12.2639V22.5355C125.326 24.6257 125.882 25.8994 126.767 26.7486H117.139C118.088 25.8341 118.35 24.5931 118.35 23.5969V13.1131C118.35 11.5781 117.843 10.8105 116.402 10.8105C115.6 10.8105 114.928 11.1045 114.486 11.5454V23.5969C114.519 24.5931 114.765 25.8015 115.731 26.7486Z"></path>
              <path d="M128.584 25.6871V19.4164C131.237 22.862 133.987 24.7726 136.558 24.7726C137.803 24.7726 138.392 24.1194 138.392 23.2049C138.392 22.3231 137.917 21.8169 137.066 21.4903L132.808 19.9226C129.534 18.7142 128.076 16.7056 128.076 13.7009C128.076 9.81434 131.269 6.98926 135.821 6.98926C138.277 6.98926 140.995 7.69145 142.354 8.67125V14.0275C140.668 11.0554 137.901 9.40609 135.232 9.40609C133.873 9.40609 133.169 9.847 133.169 10.6962C133.169 11.5453 133.676 11.8719 134.822 12.3455L139.866 14.3704C142.404 15.3992 143.533 17.5548 143.533 20.1676C143.533 24.4623 140.406 27.3201 135.707 27.3201C133.365 27.3038 130.942 26.7812 128.584 25.6871Z"></path>
              <path d="M144.27 9.34075L146.399 6.07475C149.117 1.97593 151.295 0 154.963 0C157.828 0 159.662 1.24108 159.662 3.11903C159.662 4.73569 158.336 5.91145 155.552 5.91145C155.552 3.64159 154.258 2.79243 152.981 2.79243C151.688 2.79243 150.705 3.70691 150.705 5.20926C150.705 6.23805 151.262 7.26684 152.949 7.51179H157.026L155.552 9.34075H153.047V20.2165C153.047 22.2741 153.374 24.1031 155.503 25.9647L156.387 26.7322H143.714C145.187 25.2625 146.071 23.4662 146.071 20.8371V9.34075H144.27Z"></path>
              <path d="M173.072 20.5268C171.893 25.6217 169.061 27.3037 164.951 27.3037C160.252 27.3037 155.847 23.8908 155.847 17.4894C155.847 11.333 159.744 6.9729 164.853 6.9729C168.193 6.9729 172.892 8.68755 172.892 15.9544H161.791C162.56 19.8409 165.098 21.8495 168.619 21.8495C170.436 21.8332 171.68 21.5066 173.072 20.5268ZM161.578 13.4069V13.8805H166.424C166.424 10.2226 165.622 9.07947 164.067 9.07947C162.495 9.07947 161.578 10.6635 161.578 13.4069Z"></path>
              <path d="M185.385 26.7486H173.694C175.135 25.0666 175.757 23.9562 175.757 21.2944V15.3503C175.757 13.26 175.463 12.2802 174.071 10.9902L173.269 10.2553L182.094 6.95667V11.5291C182.946 8.8836 184.255 6.95667 186.433 6.95667C188.202 6.95667 189.331 8.19774 189.331 10.0267C189.331 11.97 188.12 13.3254 186.433 13.3254C186.073 12.4762 185.189 12.0843 184.37 12.0843C183.715 12.0843 183.077 12.2639 182.749 12.5905V21.0658C182.733 23.8909 183.208 24.6584 185.385 26.7486Z"></path>
            </svg>
            </a>
        </div>
      <button
        role="button"
        className="nav__toggle"
        onClick={() => setNavToggleState((navToggleState) => !navToggleState)}
        onKeyDown={() => setNavToggleState((navToggleState = !navToggleState))}
        role="button"
        tabIndex={0}
        data-tracking-event="trackStructEvent" 
        data-tracking-action={`click_nav_toggle`} 
        data-tracking-label={`nav_toggle`}
      >
        {!navToggleState ? <span>Menu</span> : <span>Close</span>}
      </button>
      <div className="nav__drawer">
        <div className="nav__drawer__cursor" ref={cursor}>
          <div>
            <span>View</span>
            <svg viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M0.5 4.8748V6.1248H8L4.5625 9.5623L5.45 10.4498L10.4 5.4998L5.45 0.549805L4.5625 1.4373L8 4.8748H0.5Z"
                fill="white"
              />
            </svg>
          </div>
        </div>
        <div className="nav__images">
          <div className="nav__image"></div>
          <img className={`nav__image`} src={Image2021} alt="" />
          <img className={`nav__image`} src={Image2020} alt="" />
          <img className={`nav__image`} src={Image2019} alt="" />
          <img className={`nav__image`} src={Image2018} alt="" />
        </div>
        <ul className="nav__list page-links" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          <li className="nav__item">
            <div className="nav__item__line"></div>
            <a  href="/ideas-report/about" 
                className="nav__link" 
                onMouseEnter={() => setHoverIndex(0)}
                data-tracking-event="trackStructEvent" 
                data-tracking-action={`click_nav_link_about`} 
                data-tracking-label={`nav_link_about`}
            >
              About
            </a>
          </li>
          <li className="nav__item">
            <div className="nav__item__line"></div>
            <a
              href="/ideas-report/#2021"
              className="nav__link"
              onMouseEnter={() => setHoverIndex(1)}
              onClick={(ev) => close(ev, "#2021")}
              data-tracking-event="trackStructEvent" 
              data-tracking-action={`click_nav_link_2021`} 
              data-tracking-label={`nav_link_2021`}
            >
              <span>Ideas Report</span>
              <span className="nav__year">2021</span>
            </a>
          </li>
          <li className="nav__item">
            <div className="nav__item__line"></div>
            <a
              href="/ideas-report/#2020"
              className="nav__link"
              onMouseEnter={() => setHoverIndex(2)}
              onClick={(ev) => close(ev, "#2020")}
              data-tracking-event="trackStructEvent" 
              data-tracking-action={`click_nav_link_2020`} 
              data-tracking-label={`nav_link_2020`}
            >
              <span>Ideas Report</span>
              <span className="nav__year">2020</span>
            </a>
          </li>
          <li className="nav__item">
            <div className="nav__item__line"></div>
            <a
              href="/ideas-report/#2019"
              className="nav__link"
              onMouseEnter={() => setHoverIndex(3)}
              onClick={(ev) => close(ev, "#2019")}
              data-tracking-event="trackStructEvent" 
              data-tracking-action={`click_nav_link_2019`} 
              data-tracking-label={`nav_link_2019`}
            >
              <span>Ideas Report</span>
              <span className="nav__year">2019</span>
            </a>
          </li>
          <li className="nav__item">
            <div className="nav__item__line"></div>
            <a
              href="/ideas-report/#2018"
              className="nav__link"
              onMouseEnter={() => setHoverIndex(4)}
              onClick={(ev) => close(ev, "#2018")}
              data-tracking-event="trackStructEvent" 
              data-tracking-action={`click_nav_link_2019`} 
              data-tracking-label={`nav_link_2019`}
            >
              <span>Ideas Report</span>
              <span className="nav__year">2018</span>
            </a>
          </li>
        </ul>
        <div className="nav__footer">
          <div className="nav__logo nav__logo--wordmark">
            <svg viewBox="0 0 43 19.8">
              <path d="M34.9 19.8c3.2 0 5.6-1 6.8-2.3.7-.7 1.5-1.9.9-2.8-.5-.8-1.3-.9-2.3-.4-1 .5-2.1.7-3.3.7-1.2 0-2.5-.4-3.2-1.1-.8-.8-1.3-1.9-1.3-3.1 0-.3.2-.3.5-.1.9.7 2.1 1 3.9 1 3.2 0 6-2.1 6-5.4 0-3.5-3.3-6.1-8.3-6.1-5.6 0-9.9 3.6-9.9 9.7.1 5.6 3.9 9.9 10.2 9.9zM11.8 13.9c.6 0 .9.3 1.4 1l1.8 2.8c.7 1.1 1.2 1.8 2.6 1.8 1.3 0 1.9-.5 2.6-2 .8-1.8 1.6-4 2.4-6.9.8-3.3 1.2-5.3 1.2-6.9 0-1.7-.5-2.6-2.3-3-2.7-.4-6-.7-9.7-.7-3.7 0-7 .3-9.5.7C.5 1.1 0 2 0 3.7s.4 3.7 1.2 6.9c.8 2.9 1.6 5.1 2.4 6.9.6 1.5 1.2 2 2.6 2 1.3 0 1.9-.7 2.6-1.8l1.8-2.8c.3-.7.6-1 1.2-1z" />
            </svg>
          </div>
          <ul className="nav__list nav__list--footer">
            <li className="nav__item">
              <a  href="https://wetransfer.com/products/" 
                  className="nav__link"
                  data-tracking-event="trackStructEvent" 
                  data-tracking-action={`click_nav_link_footer_products`} 
                  data-tracking-label={`nav_link_footer_products`}
              >
                Products
              </a>
            </li>
            <li className="nav__item">
              <a  href="https://wetransfer.com/company/" 
                  className="nav__link"
                  data-tracking-event="trackStructEvent" 
                  data-tracking-action={`click_nav_link_footer_company`} 
                  data-tracking-label={`nav_link_footer_company`}
              >
                Company
              </a>
            </li>
            <li className="nav__item">
              <a  href="https://wetransfer.com/advertising/" 
                  className="nav__link"
                  data-tracking-event="trackStructEvent" 
                  data-tracking-action={`click_nav_link_footer_advertising`} 
                  data-tracking-label={`nav_link_footer_advertising`}
              >
                Advertising
              </a>
            </li>
            <li className="nav__item">
              <a  href="https://jobs.bywetransfer.com/" 
                  className="nav__link"
                  data-tracking-event="trackStructEvent" 
                  data-tracking-action={`click_nav_link_footer_jobs`} 
                  data-tracking-label={`nav_link_footer_jobs`}
              >
                Jobs
              </a>
            </li>
            <li className="nav__item">
              <a  href="https://wetransfer.com/blog/story/we-are-a-bcorp" 
                  className="nav__link"
                  data-tracking-event="trackStructEvent" 
                  data-tracking-action={`click_nav_link_footer_bcorp`} 
                  data-tracking-label={`nav_link_footer_bcorp`}
              >
                B Corp
              </a>
            </li>
            <li className="nav__item">
              <a  href="https://wetransfer.com/legal" 
                  className="nav__link"
                  data-tracking-event="trackStructEvent" 
                  data-tracking-action={`click_nav_link_footer_legal`} 
                  data-tracking-label={`nav_link_footer_legal`}
              >
                Legal
              </a>
            </li>
          </ul>
          <ul className="nav__list nav__list--social">
            <li className="nav__item">
              <a  href="https://twitter.com/wetransfer" 
                  className="nav__link"
                  data-tracking-event="trackStructEvent" 
                  data-tracking-action={`click_nav_link_footer_twitter`} 
                  data-tracking-label={`nav_link_footer_twitter`}
              >
                <svg fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 23 18">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M22.269.346154c-.888.519233-1.8476.879806-2.8789 1.081726C18.5021.475956 17.3993 0 16.0815 0c-1.2317 0-2.2916.447112-3.1796 1.34135-.8881.89423-1.3321 1.96153-1.3321 3.20192 0 .34616.043.6923.1289 1.03846-1.83331-.08654-3.55921-.54807-5.1777-1.38461C4.9025 3.36057 3.53467 2.23558 2.41748.822115c-.42969.692315-.64453 1.456725-.64453 2.293265 0 1.6154.67317 2.87019 2.01953 3.76443-.71615 0-1.38932-.1875-2.01953-.5625v.08654c0 1.06731.33658 2.01922 1.00976 2.85576.67319.83659 1.53971 1.37019 2.59961 1.60099-.40104.0865-.80208.1298-1.20312.1298-.28646 0-.57292-.0289-.85938-.0866.31511.9231.85221 1.6803 1.61133 2.2717.75912.5913 1.62565.887 2.59961.887-1.63282 1.2981-3.49478 1.9471-5.58594 1.9471-.34375 0-.70182-.0144-1.074215-.0433C2.99041 17.3221 5.29637 18 7.78857 18c2.00522 0 3.83853-.3894 5.50003-1.1683 1.6614-.7788 3.0149-1.7956 4.0605-3.0505 1.0456-1.2548 1.8548-2.6394 2.4278-4.1538.5729-1.51443.8593-3.03605.8593-4.5649V4.5c.8881-.66347 1.6328-1.45673 2.2344-2.37981-.8307.375-1.6901.62019-2.5781.73558.9739-.60577 1.6328-1.4423 1.9765-2.509616z"
                  />
                </svg>
              </a>
            </li>
            <li className="nav__item">
              <a  href="https://www.facebook.com/wetransfer/" 
                  className="nav__link"
                  data-tracking-event="trackStructEvent" 
                  data-tracking-action={`click_nav_link_footer_facebook`} 
                  data-tracking-label={`nav_link_footer_facebook`}
              >
                <svg fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 18">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M3.12061 0C1.87796 0 .870605 1.00736.870605 2.25v13.5c0 1.2426 1.007365 2.25 2.250005 2.25h7.89299v-6.9658H8.53857V8.31864h2.47503V6.31605c0-2.32402 1.4982-3.58949 3.6866-3.58949 1.0482 0 1.9492.07396 2.2118.10699v2.42873l-1.5179.0006c-1.1901 0-1.4205.53582-1.4205 1.32203v1.73373h2.8384l-.3696 2.71556h-2.4688V18h3.647c1.2426 0 2.25-1.0074 2.25-2.25V2.25c0-1.24264-1.0074-2.25-2.25-2.25H3.12061z"
                  />
                </svg>
              </a>
            </li>
            <li className="nav__item">
              <a  href="https://www.instagram.com/wetransfer/" 
                  className="nav__link"
                  data-tracking-event="trackStructEvent" 
                  data-tracking-action={`click_nav_link_footer_instagram`} 
                  data-tracking-label={`nav_link_footer_instagram`}
              >
                <svg fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 19 18">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M6.15992.0541599C7.11986.0103604 7.42635 0 9.87062 0c2.44428 0 2.75068.0103604 3.71068.0541599.9579.0436924 1.6122.1958471 2.1846.4183461.5919.229966 1.0938.537704 1.5942 1.038044.5003.50037.808 1.00228 1.038 1.59411.2225.57246.3747 1.22671.4183 2.18465.0438.95995.0542 1.26644.0542 3.71071 0 2.44428-.0104 2.75068-.0542 3.71068-.0436.9579-.1958 1.6122-.4183 2.1846-.23.5919-.5377 1.0938-1.038 1.5942-.5004.5003-1.0023.808-1.5942 1.038-.5724.2225-1.2267.3746-2.1846.4183-.96.0438-1.2664.0542-3.71068.0542-2.44427 0-2.75076-.0104-3.7107-.0542-.95795-.0437-1.61219-.1958-2.18466-.4183-.59183-.23-1.09373-.5377-1.59411-1.038-.50033-.5004-.80807-1.0023-1.03804-1.5942-.2225-.5724-.374652-1.2267-.418345-2.1846-.043799-.96-.05416-1.2664-.05416-3.71068 0-2.44427.010361-2.75076.05416-3.71071.043693-.95794.195845-1.61219.418345-2.18465.22997-.59183.53771-1.09374 1.03804-1.59411.50038-.50034 1.00228-.808078 1.59411-1.038044C4.54773.250007 5.20197.0978523 6.15992.0541599zM13.5071 1.67386c-.949-.0433-1.2337-.05248-3.63673-.05248-2.40311 0-2.68777.00918-3.63678.05248-.87749.04002-1.35403.18663-1.67117.30989-.42009.16326-.7199.35829-1.03482.67324-.31496.31492-.50998.61473-.67325 1.03483-.12325.31713-.26987.79367-.30988 1.67116-.0433.94902-.05248 1.23368-.05248 3.63679 0 2.40303.00918 2.68773.05248 3.63673.04001.8775.18663 1.354.30988 1.6712.16327.4201.35833.7199.67325 1.0348.31492.315.61473.51 1.03482.6733.31714.1232.79368.2698 1.67117.3098.9489.0433 1.23349.0525 3.63678.0525 2.40323 0 2.68793-.0092 3.63673-.0525.8775-.04 1.3541-.1866 1.6712-.3098.4201-.1633.7199-.3583 1.0348-.6733.315-.3149.51-.6147.6733-1.0348.1232-.3172.2698-.7937.3098-1.6712.0433-.949.0525-1.2337.0525-3.63673 0-2.40311-.0092-2.68777-.0525-3.63679-.04-.87749-.1866-1.35403-.3098-1.67116-.1633-.4201-.3583-.71991-.6733-1.03483-.3149-.31495-.6147-.50998-1.0348-.67324-.3171-.12326-.7937-.26987-1.6712-.30989zM5.24907 9.00009c0-2.55248 2.06915-4.62163 4.62163-4.62163 2.5524 0 4.6216 2.06915 4.6216 4.62163 0 2.55241-2.0692 4.62161-4.6216 4.62161-2.55248 0-4.62163-2.0692-4.62163-4.62161zm1.62138-.00025c0 1.65686 1.34313 2.99996 3 2.99996 1.65685 0 2.99995-1.3431 2.99995-2.99996 0-1.65687-1.3431-2.99999-2.99995-2.99999-1.65687 0-3 1.34312-3 2.99999zm8.88285-4.80263c0 .59647-.4836 1.07998-1.0801 1.07998-.5964 0-1.0799-.48351-1.0799-1.07998 0-.59648.4835-1.08002 1.0799-1.08002.5965 0 1.0801.48354 1.0801 1.08002z"
                  />
                </svg>
              </a>
            </li>
            <li className="nav__item">
              <a  href="https://www.youtube.com/channel/UCFHZRaJORf-Pffh2rnF8WaQ" 
                  className="nav__link"
                  data-tracking-event="trackStructEvent" 
                  data-tracking-action={`click_nav_link_footer_youtube`} 
                  data-tracking-label={`nav_link_footer_youtube`}
              >
                <svg fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 16">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M23.6403 3.45185s-.2237-1.56817-.9138-2.25854C21.8521.287262 20.8712.282552 20.422.229809 17.2028 0 12.3749 0 12.3749 0h-.0096S7.53747 0 4.31825.229809c-.44823.052743-1.43012.057453-2.30355.963501C1.32556 1.88368 1.1 3.45185 1.1 3.45185S.870605 5.29126.870605 7.13256v1.7264c0 1.84134.230355 3.68074.230355 3.68074s.2246 1.5672.91374 2.2576c.87535.907 2.02425.8778 2.53487.9738C6.38953 15.9444 12.3701 16 12.3701 16s4.8327-.0066 8.0529-.2383c.4501-.0537 1.4301-.0574 2.3035-.9644.6892-.6904.9138-2.2576.9138-2.2576s.2303-1.8413.2303-3.68074v-1.7264c0-1.8413-.2303-3.68071-.2303-3.68071zM9.99459 10.9489V4.55851l6.21481 3.20697-6.21481 3.18342z"
                  />
                </svg>
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Navigation;